/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
import { logger, sanitizeError } from "../utils/logger.js";
/**
 * @fileoverview CompanyProfile page component for displaying detailed company information.
 * This component renders the complete company profile view including:
 * - Company header with logo, name, and basic info
 * - Profile selector for switching between different company profiles (Perfil 1, 2, 3)
 * - Progress bar showing profile completion percentage
 * - Tabbed sections for General Info, Contact/Location, Products/Services, and Networking
 * - Sidebar with contact details, digital presence, keywords, and certifications
 *
 * @module pages/CompanyProfile
 * @requires react
 * @requires react-select
 * @requires react-router
 * @requires react-i18next
 */

import React, { useCallback, useEffect, useState, useRef, useMemo } from "react";
import Select from "react-select";
import styles from "../styles/CompanyProfile.module.css";
import { useLocation, useNavigate } from "react-router";
import AssetImage from "../components/AssetImage";
import LanguageSelector from "../components/LanguageSelector";
import NotificationBell from "../components/NotificationBell";
import SectionTabs from "../components/SectionTabs";
import { 
  getCompanySizeOptions,
  getCompanyGeneralActivityOptions,
  getCertificationsOptions,
  getCountryCodeOptions } from "../data/selectOptions";
import {
  getSelectedOption,
  getSelectedOptionWithZeroFirst,
  getSelectedCountryCodeOption,
  getCompanyFieldValue,
  shouldHideFieldForProfile,
  getReferencedDataOptionById,
} from "../utils/common.js";
import { useTranslation } from "react-i18next";
import { useNavigation } from "../context/NavigationContext";
import { getUser } from "../logic/user";
import {
  sendConnectionRequest,
  checkCompanyConnectionStatus,
} from "../logic/connection";
import { getCompanyByRFC, getCompanyById } from "../logic/company";
import { getBusinessEventsForBuyer } from "../logic/businessEvents";
import {
  calculateProgressStep,
  getReferenceData,
  getReferenceDataById,
} from "../logic/companyEdit";
import { getSectorById } from "../logic/proposal/index.js";
import EventInvitationModal from "../modals/EventInvitationModal";
import {
  CompanyProfileCompletion,
  CompanyProfileHeading,
  CompanyProfileOwnerActions,
  CompanyProfileSidebar,
  CompanyProfileTabletSections,
} from "./CompanyProfilePanels.jsx";

import { CircularProgress, Backdrop } from "@mui/material";

import AboutCompany from "../components/CompanyProfileSections/FirstTab/AboutCompany";
import ContactsData from "../components/CompanyProfileSections/SecondTab/ContactsData";
import Location from "../components/CompanyProfileSections/SecondTab/Location";
import Branch from "../components/CompanyProfileSections/SecondTab/Branch";
import CommercialOffer from "../components/CompanyProfileSections/ThirdTab/CommercialOffer";
import Skills from "../components/CompanyProfileSections/ThirdTab/Skills";
import Documents from "../components/CompanyProfileSections/ThirdTab/Documents";
import Networking from "../components/CompanyProfileSections/FourthTab/Networking";

import { ReactComponent as ChevronLeftIcon } from "../assets/icons/CompanyProfile/ChevronLeftIcon.svg";

/**
 * Section ID constants for the company profile tabs.
 * These constants are used to identify and navigate between different sections
 * of the company profile page.
 *
 * @constant {string} SECTION_GENERAL_INFO - General information section identifier
 * @constant {string} SECTION_CONTACT_LOCATION - Contact and location section identifier
 * @constant {string} SECTION_PRODUCTS_SERVICES - Products and services section identifier
 * @constant {string} SECTION_NETWORKING - Networking and linkage section identifier
 */
const DIRECTORY_ROUTE = "/directory";
const DEFAULT_SELECTED_PROFILE = 0;
const SECTION_GENERAL_INFO = "informacion-general";
const SECTION_CONTACT_LOCATION = "contacto-ubicacion";
const SECTION_PRODUCTS_SERVICES = "productos-servicios";
const SECTION_NETWORKING = "vinculacion-networking";

/**
 * CompanyProfile component - Displays a comprehensive view of a company's profile.
 *
 * This component provides a detailed view of company information organized into
 * multiple sections accessible via tabs. It supports three different profile views
 * (Perfil 1, 2, 3) that can show/hide different fields based on profile configuration.
 *
 * Features:
 * - Profile switcher with 3 available profiles
 * - Progress bar showing profile completion percentage
 * - Four main sections: General Info, Contact/Location, Products/Services, Networking
 * - Sidebar with quick contact info, social media links, keywords, and certifications
 * - Connection request functionality for networking
 * - Event invitation capability for buyers
 *
 * @component
 * @returns {JSX.Element|null} The rendered company profile page or null if no company data
 *
 * @example
 * // Navigate to company profile with company id
 * navigate('/companyProfile', { state: { companyId: companyData._id } });
 */
const resolveCompanyIdFromLocation = (state) =>
  state?.companyId ??
  state?.company?._id ??
  state?.company?.id ??
  state?.company?.idEmpresa ??
  null;

const CompanyProfile = () => {
  // Translation and routing hooks
  const { t, i18n } = useTranslation();
  const countryCodeOptions = getCountryCodeOptions(t);
  const location = useLocation();
  const navigate = useNavigate();
  const { updateNavigation } = useNavigation();

  // UI state management
  /** @type {[string, Function]} Currently active section tab */
  const [activeSection, setActiveSection] = useState(SECTION_GENERAL_INFO);
  /** @type {React.RefObject} Reference to top of page for scroll behavior */
  const topRef = useRef(null);
  /** @type {[boolean, Function]} Loading state for initial data fetch */
  const [loading, setLoading] = useState(true);
  /** @type {[Array, Function]} States reference data for address display */
  const [states, setStates] = useState([]);
  /** @type {[Array, Function]} Towns reference data for address display */
  const [towns, setTowns] = useState([]);

  const [locationRefs, setLocationRefs] = useState({
    country: null,
    state: null,
    town: null,
    zipCode: null,
    neighborhood: null,
  });

  // User and company state
  /** @type {[Object|null, Function]} Company profile data fetched by id */
  const [company, setCompany] = useState(null);
  /** @type {[Object|null, Function]} Current logged-in user data */
  const [user, setUser] = useState(null);
  /** @type {[Object|null, Function]} Current user's company data */
  const [userCompany, setUserCompany] = useState(null);
  /** @type {[string|null, Function]} Company sector name or null if hidden */
  const [companySectorName, setCompanySectorName] = useState(null);

  // Connection state management
  /** @type {[boolean, Function]} Whether user is connected to this company */
  const [isConnected, setIsConnected] = useState(false);
  /** @type {[boolean, Function]} Loading state for connection operations */
  const [connectionLoading, setConnectionLoading] = useState(false);
  /** @type {[boolean, Function]} Loading state for company data fetch */
  const [companyDataLoading, setCompanyDataLoading] = useState(false);

  // Event invitation state
  /** @type {[boolean, Function]} Controls event invitation modal visibility */
  const [isEventInvitationModalOpen, setIsEventInvitationModalOpen] =
    useState(false);
  /** @type {[Array, Function]} List of available events for invitation */
  const [availableEvents, setAvailableEvents] = useState([]);
  /** @type {[boolean, Function]} Loading state for event invitation operations */
  const [eventInvitationLoading, setEventInvitationLoading] = useState(false);
  /** @type {React.RefObject} Reference to event invitation dialog */
  const eventInvitationDialogRef = useRef(null);

  // Select options for dropdowns
  /** @type {Array<{value: string, label: string}>} Company size options for display */
  const companySizeOptions = getCompanySizeOptions(t);
  /** @type {Array<{value: string, label: string}>} Company general activity options for display */
  const companyGeneralActivityOptions = getCompanyGeneralActivityOptions(t);
  /** @type {Array<{value: string, label: string}>} Certification options for display */
  const certificationsOptions = getCertificationsOptions(t);
  const companyIdFromState = resolveCompanyIdFromLocation(location.state);

  /**
   * Profile options for the profile selector buttons.
   * Each profile can have different visibility settings for company fields.
   *
   * @type {Array<{value: number, label: string}>}
   * @property {number} value - Profile index (0, 1, or 2)
   * @property {string} label - Translated profile label
   */
  const profileOptions = [
    { value: 0, label: t("pages.companyProfile.profile1") },
    { value: 1, label: t("pages.companyProfile.profile2") },
    { value: 2, label: t("pages.companyProfile.profile3") },
  ];

  /**
   * Currently selected profile index.
   * Controls which profile's field visibility settings are applied.
   *
   * @type {[number, Function]}
   * @default 0 - Main profile (Perfil 1)
   */
  const [selectedProfile, setSelectedProfile] = useState(
    location.state?.selectedProfile ?? DEFAULT_SELECTED_PROFILE,
  );

  useEffect(() => {
    if (!companyIdFromState) {
      navigate(DIRECTORY_ROUTE);
      setLoading(false);
      return () => {};
    }

    let cancelled = false;

    const fetchCompanyData = async () => {
      setLoading(true);
      setCompany(null);

      try {
        const companyData = await getCompanyById(companyIdFromState, navigate);
        if (cancelled) {
          return;
        }

        if (!companyData || companyData?.message) {
          navigate(DIRECTORY_ROUTE);
          return;
        }

        setCompany(companyData);

        if (location.state?.selectedProfile != null) {
          setSelectedProfile(location.state.selectedProfile);
        } else if (companyData?.perfilSeleccionado != null) {
          setSelectedProfile(companyData.perfilSeleccionado);
        } else {
          setSelectedProfile(DEFAULT_SELECTED_PROFILE);
        }
      } catch (error) {
        logger.error("Error fetching company data:", {
          err: sanitizeError(error),
        });
        if (!cancelled) {
          navigate(DIRECTORY_ROUTE);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchCompanyData();

    return () => {
      cancelled = true;
    };
  }, [companyIdFromState, location.state?.selectedProfile, navigate]);

  /**
   * Profile-aware field values using useMemo for performance optimization.
   * These memoized values update when company data or selected profile changes.
   */

  /**
   * Company address data based on selected profile.
   * @type {Array} Array of address objects for the company
   */
  const address = useMemo(() => {
    return getCompanyFieldValue(company, "direccion", selectedProfile, []);
  }, [company, selectedProfile]);

  // Fetch states and towns reference data for address display (municipio/estado labels)
  useEffect(() => {
    if (!company?.direccion?.[1]) {
      return () => {};
    }

    let cancelled = false;
    const fetchReferenceData = async () => {
      try {
        const [
          statesData,
          townsData,
          countryData,
          stateData,
          townData,
          zipCodeData,
          neighborhoodData,
          sectorData,
        ] = await Promise.all([
          getReferenceData("states", navigate),
          getReferenceData("towns", navigate),
          getReferenceDataById(
            "countries",
            company.direccion[1].pais,
            navigate,
          ),
          getReferenceDataById("states", company.direccion[1].estado, navigate),
          getReferenceDataById(
            "towns",
            company.direccion[1].municipio,
            navigate,
          ),
          getReferenceDataById(
            "zipCodes",
            company.direccion[1].codigoPostal,
            navigate,
          ),
          getReferenceDataById(
            "neighborhoods",
            company.direccion[1].colonia,
            navigate,
          ),
          getSectorById(company?.sectorEconomico, navigate),
        ]);
        if (!cancelled) {
          setStates(Array.isArray(statesData) ? statesData : []);
          setTowns(Array.isArray(townsData) ? townsData : []);
          setLocationRefs({
            country: countryData.data,
            state: stateData.data,
            town: townData.data,
            zipCode: zipCodeData.data,
            neighborhood: neighborhoodData.data,
          });
          setCompanySectorName(sectorData?.nombre || "[Sector]");
        }
      } catch (err) {
        logger.error("Error fetching states/towns for company profile:", {
          err: sanitizeError(err),
        });
        if (!cancelled) {
          setStates([]);
          setTowns([]);
          setLocationRefs({
            country: null,
            state: null,
            town: null,
            zipCode: null,
            neighborhood: null,
          });
        }
      }
    };
    fetchReferenceData();
    return () => {
      cancelled = true;
    };
  }, [navigate, company]);

  /**
   * Location label for header: town and state names resolved from reference data.
   * @type {string}
   */
  const companyLocationLabel = useMemo(() => {
    const addr = address[1];
    if (!addr) {
      return "";
    }
    const townOption = getReferencedDataOptionById(towns, addr.municipio);
    const stateOption = getReferencedDataOptionById(states, addr.estado);
    const parts = [townOption?.nombre, stateOption?.nombre].filter(Boolean);
    return parts.join(", ");
  }, [address, states, towns]);

  /**
   * Formatted company start date, respecting profile visibility settings.
   * @type {string} Date string in DD/MM/YYYY format or empty if hidden
   */
  const companyStartDate = useMemo(() => {
    if (shouldHideFieldForProfile("fechaAltaSac", selectedProfile)) {
      return "";
    }
    const fecha = company?.fechaAltaSac;
    return fecha ? new Date(fecha).toLocaleDateString("en-GB") : "";
  }, [company, selectedProfile]);

  /**
   * Company's general activity label based on selected profile.
   * Resolves the option label from actividadGeneralDeLaEmpresa using companyGeneralActivityOptions
   * (getSelectedOptionWithZeroFirst handles stored value 0 correctly).
   * @type {string} General activity display label
   */
  const companyGeneralActivity = useMemo(() => {
    const value = getCompanyFieldValue(
      company,
      "actividadGeneralDeLaEmpresa",
      selectedProfile,
      null,
    );
    const option = getSelectedOptionWithZeroFirst(
      companyGeneralActivityOptions,
      value,
    );
    return option?.label ?? "";
  }, [company, selectedProfile, companyGeneralActivityOptions]);

  /**
   * Selected company size option for display.
   * @type {Object|null} Selected option object with value and label
   */
  const companySizeSelectedOption = useMemo(() => {
    const size = getCompanyFieldValue(
      company,
      "tamanoDeLaEmpresa",
      selectedProfile,
      null,
    );
    return getSelectedOption(companySizeOptions, size);
  }, [company, selectedProfile, companySizeOptions]);

  /** @type {string} Display label for company size */
  const companySizeLabel = companySizeSelectedOption?.label || "";

  /**
   * Company sector based on selected profile visibility settings.
   * @type {string|null} Sector name or null if hidden
   */
  const companySector = useMemo(() => {
    if (shouldHideFieldForProfile("sectorEconomico", selectedProfile)) {
      return null;
    }
    return companySectorName || "[Sector]";
  }, [companySectorName, selectedProfile]);

  // Contact information fields based on selected profile
  /** @type {string} Company office phone number */
  const companyOfficePhone = getCompanyFieldValue(
    company,
    "telefonoDeOficina",
    selectedProfile,
    "",
  );
  /** @type {string} Country code for office phone */
  const companyOfficePhoneCountryCode = getSelectedCountryCodeOption(
    countryCodeOptions,
    getCompanyFieldValue(
      company,
      "telefonoDeOficinaCodigoPais",
      selectedProfile,
      null,
    ),
  )?.code;
  /** @type {string} Company secondary phone number */
  const companySecondaryPhone = getCompanyFieldValue(
    company,
    "telefonoSecundario",
    selectedProfile,
    "",
  );
  /** @type {string} Country code for secondary phone */
  const companySecondaryPhoneCountryCode = getSelectedCountryCodeOption(
    countryCodeOptions,
    getCompanyFieldValue(
      company,
      "telefonoSecundarioCodigoPais",
      selectedProfile,
      null,
    ),
  )?.code;
  /** @type {string} Phone extension number */
  const companyExtension = getCompanyFieldValue(
    company,
    "extensionTelefono",
    selectedProfile,
    "",
  );
  /** @type {string} Primary company email address */
  const companyEmail = getCompanyFieldValue(
    company,
    "correoElectronicoPrincipal",
    selectedProfile,
    "",
  );
  /** @type {string} Company website URL */
  const companyWebsite = getCompanyFieldValue(
    company,
    "paginaInternet",
    selectedProfile,
    "",
  );

  // Social media profile URLs based on selected profile
  /** @type {string} Twitter/X profile URL */
  const companyTwitter = getCompanyFieldValue(
    company,
    "twitter",
    selectedProfile,
    "",
  );
  /** @type {string} YouTube channel URL */
  const companyYoutube = getCompanyFieldValue(
    company,
    "youtube",
    selectedProfile,
    "",
  );
  /** @type {string} Facebook page URL */
  const companyFacebook = getCompanyFieldValue(
    company,
    "facebook",
    selectedProfile,
    "",
  );
  /** @type {string} LinkedIn profile URL */
  const companyLinkedin = getCompanyFieldValue(
    company,
    "linkedIn",
    selectedProfile,
    "",
  );
  /** @type {string} Instagram profile URL */
  const companyInstagram = getCompanyFieldValue(
    company,
    "instagram",
    selectedProfile,
    "",
  );

  /**
   * Company keywords organized by language.
   * Ensures all supported languages have an array, even if empty.
   *
   * @type {Object}
   * @property {Array<string>} es - Spanish keywords
   * @property {Array<string>} en - English keywords
   * @property {Array<string>} fr - French keywords
   */
  const keywords = useMemo(() => {
    try {
      const companyKeywords = getCompanyFieldValue(
        company,
        "palabrasClave",
        selectedProfile,
        {},
      );

      // Ensure all supported languages have an array
      const languages = ["es", "en", "fr"];

      languages.forEach((lang) => {
        if (!companyKeywords[lang]) {
          companyKeywords[lang] = [];
        }
      });

      return companyKeywords;
    } catch (error) {
      logger.error("Error parsing keywords:", { err: sanitizeError(error) });
      return {
        es: [],
        en: [],
        fr: [],
      };
    }
  }, [company, selectedProfile]);
  /** @type {[string, Function]} Current language for displaying localized content */
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language);

  /**
   * State for sidebar section collapse/expand status.
   * Controls which sidebar sections are expanded or collapsed.
   *
   * @type {[Object, Function]}
   */
  const [collapsedSections, setCollapsedSections] = useState({
    generalActivity: true,
    keywords: true,
    certifications: true,
  });

  /**
   * State for main content section collapse/expand status.
   * Used primarily for mobile/tablet responsive views.
   *
   * @type {[Object, Function]}
   */
  const [collapsedMainSections, setCollapsedMainSections] = useState({
    SECTION_GENERAL_INFO: true,
    SECTION_CONTACT_LOCATION: true,
    SECTION_PRODUCTS_SERVICES: true,
    SECTION_NETWORKING: true,
  });

  /**
   * Profile completion percentage as a string.
   * Calculated based on filled fields for the selected profile (0-100).
   * Each step represents 25% completion.
   *
   * @type {string}
   */
  const progressPercentage = (
    calculateProgressStep(company, selectedProfile) * 25
  ).toString();

  /**
   * Array of certification labels for the selected profile.
   * Maps certification IDs to their translated display labels.
   *
   * @type {Array<string>}
   */
  const certifications = useMemo(() => {
    try {
      const certificationIds = getCompanyFieldValue(
        company,
        "certificaciones",
        selectedProfile,
        [],
      );
      return certificationIds
        .map((id) => {
          const option = certificationsOptions.find((opt) => opt.value === id);
          return option?.label || "";
        })
        .filter((label) => label !== "");
    } catch (error) {
      logger.error("Error parsing certifications:", {
        err: sanitizeError(error),
      });
      return [];
    }
  }, [company, selectedProfile, certificationsOptions]);

  /**
   * Visibility flags for social media links and contact information.
   * These flags control whether specific fields should be displayed
   * based on the selected profile configuration.
   */
  /** @type {boolean} Whether to show LinkedIn link in sidebar */
  const showLinkedIn = getCompanyFieldValue(
    company,
    "mostrarLinkedIn",
    selectedProfile,
    false,
  );
  /** @type {boolean} Whether to show Facebook link in sidebar */
  const showFacebook = getCompanyFieldValue(
    company,
    "mostrarFacebook",
    selectedProfile,
    false,
  );
  /** @type {boolean} Whether to show Twitter/X link in sidebar */
  const showTwitter = getCompanyFieldValue(
    company,
    "mostrarTwitter",
    selectedProfile,
    false,
  );
  /** @type {boolean} Whether to show Instagram link in sidebar */
  const showInstagram = getCompanyFieldValue(
    company,
    "mostrarInstagram",
    selectedProfile,
    false,
  );
  /** @type {boolean} Whether to show YouTube link in sidebar */
  const showYoutube = getCompanyFieldValue(
    company,
    "mostrarYoutube",
    selectedProfile,
    false,
  );
  /** @type {boolean} Whether to show office phone in sidebar */
  const showOfficePhone = getCompanyFieldValue(
    company,
    "mostrarTelefonoDeOficina",
    selectedProfile,
    false,
  );
  /** @type {boolean} Whether to show secondary phone in sidebar */
  const showSecondaryPhone = getCompanyFieldValue(
    company,
    "mostrarTelefonoSecundario",
    selectedProfile,
    false,
  );
  /** @type {boolean} Whether to show phone extension in sidebar */
  const showPhoneExtension = getCompanyFieldValue(
    company,
    "mostrarExtensionTelefono",
    selectedProfile,
    false,
  );
  /** @type {boolean} Whether to show email address in sidebar */
  const showEmail = getCompanyFieldValue(
    company,
    "mostrarCorreoElectronicoPrincipal",
    selectedProfile,
    false,
  );
  /** @type {boolean} Whether to show website URL in sidebar */
  const showWebsite = getCompanyFieldValue(
    company,
    "mostrarPaginaInternet",
    selectedProfile,
    false,
  );

  /**
   * Effect: Initial data loading and navigation setup.
   *
   * This effect runs on component mount and when company/navigate/context changes.
   * It handles:
   * - Redirecting to directory if no company data
   * - Updating navigation context
   * - Scrolling to top of page
   * - Fetching current user data
   * - Fetching user's company data for connection features
   */
  useEffect(() => {
    if (!company) {
      return;
    }

    updateNavigation("/companyProfile", location.state?.context || "myCompany");

    topRef.current?.scrollIntoView({ behavior: "smooth" });

    /**
     * Fetches the current user's data from the API.
     * Also triggers fetch of user's company data if RFC is available.
     *
     * @async
     * @inner
     */
    const fetchUserData = async () => {
      const userId = localStorage.getItem("userId");
      if (!userId) {
        setLoading(false);
        return;
      }

      try {
        const userData = await getUser(userId, navigate);
        if (userData) {
          setUser(userData);
          // Fetch user's company data if RFC is available
          if (userData.RFC) {
            await fetchUserCompanyData(userData.RFC);
          }
        }
      } catch (error) {
        logger.error("Error fetching user data:", {
          err: sanitizeError(error),
        });
      } finally {
        setLoading(false);
      }
    };

    /**
     * Fetches company data for the current user by RFC.
     * Used to populate sender company info for connection requests.
     *
     * @async
     * @inner
     * @param {string} userRFC - The RFC identifier of the user's company
     */
    const fetchUserCompanyData = async (userRFC) => {
      setCompanyDataLoading(true);
      try {
        const companyData = await getCompanyByRFC(userRFC, navigate);
        if (companyData) {
          setUserCompany(companyData);
        }
      } catch (error) {
        logger.error("Error fetching user company data:", {
          err: sanitizeError(error),
        });
      } finally {
        setCompanyDataLoading(false);
      }
    };

    fetchUserData();
  }, [company, navigate, location.state?.context]);

  /**
   * Effect: Language synchronization.
   * Updates the currentLanguage state when i18n language changes.
   * Used to display keywords in the correct language.
   */
  useEffect(() => {
    setCurrentLanguage(i18n.language);
  }, [i18n.language]);

  /**
   * Effect: Connection status check.
   *
   * Checks if the current user has an existing connection request
   * with the displayed company. Only runs if:
   * - Both user and company have RFC identifiers
   * - User is not viewing their own company
   *
   * Updates the isConnected state based on API response.
   */
  useEffect(() => {
    /**
     * Async function to check connection status between companies.
     * @async
     * @inner
     */
    const checkConnectionStatus = async () => {
      // Skip check if viewing own company or missing data
      if (!user?.RFC || !company?.RFC || user.RFC === company.RFC) {
        return;
      }

      setConnectionLoading(true);
      try {
        const connectionStatus = await checkCompanyConnectionStatus(
          user.RFC,
          company.RFC,
          navigate,
        );

        logger.info("connectionStatus: ", { data: connectionStatus });

        setIsConnected(connectionStatus.hasRecentConnectionRequest);
      } catch (error) {
        logger.error("Error checking connection status:", {
          err: sanitizeError(error),
        });
        setIsConnected(false);
      } finally {
        setConnectionLoading(false);
      }
    };

    checkConnectionStatus();
  }, [user, company, navigate]);

  /**
   * Handles section tab change in the profile view.
   * Updates the active section to display different content.
   *
   * @param {string} sectionId - The ID of the section to activate
   */
  const handleSectionChange = (sectionId) => {
    setActiveSection(sectionId);
  };

  /**
   * Handles navigation back to the previous page.
   * Determines the correct destination based on navigation context.
   *
   * Possible destinations:
   * - Company edit page (if coming from edit)
   * - Directory page (if context is directory)
   * - View appointments page (if context is appointments)
   * - Chat page (if context is chat)
   * - Home page (default for myCompany context)
   */
  const handleBack = () => {
    const from = location.state?.from;
    const context = location.state?.context;

    if (from === "companyEdit") {
      // Return to company edit
      navigate(`/companyEdit/${company._id}`, {
        state: {
          from: "companyProfile",
          context: context || "myCompany",
        },
      });
    } else {
      // Navigate back based on context or from
      switch (context) {
        case "directory":
          navigate(-1);
          break;
        case "appointments":
          navigate("/viewAppointments", {
            state: {
              event: location.state?.event,
            },
          });
          break;
        case "chat":
          navigate("/chat");
          break;
        case "myCompany":
        default:
          // Default to home for myCompany context
          navigate("/home");
          break;
      }
    }
  };

  /**
   * Handles click on the edit profile button.
   * Navigates to the company edit page with current profile selection.
   */
  const handleEditClick = useCallback(() => {
    navigate(`/companyEdit/${company._id}`, {
      state: {
        from: "companyProfile",
        context: location.state?.context || "myCompany",
        selectedProfile: selectedProfile,
      },
    });
  }, [company?._id, location.state?.context, navigate, selectedProfile]);

  /**
   * Handles profile selection change from the profile buttons.
   * Updates the selected profile to show different field visibility configurations.
   *
   * @param {Object} option - The selected profile option
   * @param {number} option.value - The profile index (0, 1, or 2)
   */
  const handleProfileChange = useCallback((option) => {
    setSelectedProfile(option.value);
  }, []);

  /**
   * Handles click on the connect button.
   * Sends a connection request to the displayed company.
   *
   * @async
   */
  const handleConnectClick = useCallback(async () => {
    setIsConnected(true);

    const connectionRequestData = {
      senderUserId: user._id,
      senderCompanyRFC: user.RFC,
      senderCompanyName: userCompany?.nombreComercial || t("common.defaultCompanyName"),
      recipientCompanyRFC: company.RFC,
      senderUserType: user.tipoDeUsuario,
    };

    const result = await sendConnectionRequest(connectionRequestData);
    if (result) {
      logger.info("Connection request sent successfully");
    } else {
      logger.error("Failed to send connection request");
      setIsConnected(false);
    }
  }, [company?.RFC, t, user, userCompany?.nombreComercial]);

  /**
   * Handles click on the invite to event button.
   * Fetches available events for the buyer and opens the invitation modal.
   *
   * @async
   * @returns {Promise<void>} Resolves when events are fetched and modal is opened
   */
  const handleInviteToEventClick = useCallback(async () => {
    if (!user?._id) {
      return;
    }

    setEventInvitationLoading(true);
    try {
      const events = await getBusinessEventsForBuyer(user._id, navigate);
      setAvailableEvents(events);
      setIsEventInvitationModalOpen(true);
    } catch (error) {
      logger.error("Error fetching events:", { err: sanitizeError(error) });
    } finally {
      setEventInvitationLoading(false);
    }
  }, [navigate, user?._id]);

  /**
   * Handles confirmation of event invitation selection.
   * Sends a connection request with event invitation details.
   *
   * @async
   * @param {Object} selectedEventOption - The selected event option from the modal
   * @param {Object} selectedEventOption.value - Event data including IDs
   * @returns {Promise<void>} Resolves when invitation is sent
   */
  const handleEventInvitationConfirm = async (selectedEventOption) => {
    if (!selectedEventOption || !selectedEventOption.value || !user) {
      return;
    }

    const selectedEventData = selectedEventOption.value;
    setEventInvitationLoading(true);
    const connectionRequestData = {
      senderUserId: user._id,
      senderCompanyRFC: user.RFC,
      senderCompanyName:
        userCompany?.nombreComercial || t("common.defaultCompanyName"),
      recipientCompanyRFC: company.RFC,
      senderUserType: user.tipoDeUsuario,
      participatingCompanyId: selectedEventData.participatingCompanyId,
      businessEventRequirementId: selectedEventData.businessEventRequirementId,
      businessEventId: selectedEventData.event?._id,
    };

    const result = await sendConnectionRequest(connectionRequestData);
    if (result) {
      logger.info("Event invitation connection request sent successfully");
      setIsConnected(true);
      setIsEventInvitationModalOpen(false);
    } else {
      logger.error("Failed to send event invitation connection request");
      setIsEventInvitationModalOpen(false);
    }

    setEventInvitationLoading(false);
  };

  /**
   * Toggles the collapsed state of a sidebar section.
   * Used for expandable sections like keywords and certifications.
   *
   * @param {string} sectionName - Name of the section to toggle
   */
  const toggleSection = useCallback((sectionName) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [sectionName]: !prev[sectionName],
    }));
  }, []);

  /**
   * Toggles the collapsed state of a main content section.
   * Used for the main profile sections on mobile/tablet views.
   *
   * @param {string} sectionName - Name of the main section to toggle
   */
  const toggleMainSection = useCallback((sectionName) => {
    setCollapsedMainSections((prev) => ({
      ...prev,
      [sectionName]: !prev[sectionName],
    }));
  }, []);

  const handleCloseEventInvitation = useCallback(() => {
    setIsEventInvitationModalOpen(false);
  }, []);

  // Early return if company data is still loading or unavailable
  if (!company) {
    return (
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(4px)",
        }}
        open={loading || companyDataLoading}
      >
        <CircularProgress
          size={60}
          thickness={4}
          sx={{
            color: "#4eabe7",
          }}
        />
      </Backdrop>
    );
  }

  /**
   * Configuration array for the section tabs.
   * Defines the available sections and their translated labels.
   *
   * @type {Array<{id: string, label: string}>}
   */
  const sections = [
    { id: SECTION_GENERAL_INFO, label: t("pages.companyProfile.section1") },
    { id: SECTION_CONTACT_LOCATION, label: t("pages.companyProfile.section2") },
    {
      id: SECTION_PRODUCTS_SERVICES,
      label: t("pages.companyProfile.section3"),
    },
    { id: SECTION_NETWORKING, label: t("pages.companyProfile.section4") },
  ];

  /**
   * Renders the content for the currently active section tab.
   * Returns the appropriate component(s) based on activeSection state.
   *
   * @returns {JSX.Element|null} The rendered section content or null
   */
  const renderSectionContent = () => {
    switch (activeSection) {
      case SECTION_GENERAL_INFO:
        return (
          <AboutCompany company={company} selectedProfile={selectedProfile} />
        );
      case SECTION_CONTACT_LOCATION:
        return (
          <div className={styles.sectionsContainer}>
            <ContactsData company={company} selectedProfile={selectedProfile} />
            <Location
              company={company}
              locationRefs={locationRefs}
              selectedProfile={selectedProfile}
            />
            <Branch company={company} selectedProfile={selectedProfile} />
          </div>
        );
      case SECTION_PRODUCTS_SERVICES:
        return (
          <div className={styles.sectionsContainer}>
            <CommercialOffer
              company={company}
              selectedProfile={selectedProfile}
            />
            <Skills company={company} selectedProfile={selectedProfile} />
            <Documents company={company} selectedProfile={selectedProfile} />
          </div>
        );
      case SECTION_NETWORKING:
        return (
          <div className={styles.sectionsContainer}>
            <Networking company={company} selectedProfile={selectedProfile} />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className={styles.container} ref={topRef}>
      <div className={styles.header}>
        <div className={styles.languages}>
          <NotificationBell
            className={styles.notificationBell}
            navigate={navigate}
          />
          <LanguageSelector className={styles.languageSelector} />
        </div>

        {/* Back button and title */}
        <div className={styles.headerInfoContainer}>
          <div className={styles.headerInfo}>
            <button
              className={styles.returnButtonContainer}
              onClick={handleBack}
            >
              <ChevronLeftIcon className={styles.returnButtonIcon} />
              <span className={styles.returnButtonText}>
                {t("common.goBack")}
              </span>
            </button>
          </div>
        </div>

        <div className={styles.headerBanner}>
          <div className={styles.logoContainer}>
            <AssetImage
              src={company.logo || ""}
              alt={t("common.companyLogoAlt")}
              className={styles.companyLogo}
            />
          </div>
        </div>
      </div>
      <div className={styles.businessProfile}>
        <div className={styles.profileHeader}>
          <CompanyProfileHeading
            t={t}
            company={company}
            selectedProfile={selectedProfile}
            companySizeLabel={companySizeLabel}
            companyLocationLabel={companyLocationLabel}
            companyStartDate={companyStartDate}
          />
          <CompanyProfileOwnerActions
            t={t}
            user={user}
            company={company}
            isConnected={isConnected}
            connectionLoading={connectionLoading}
            eventInvitationLoading={eventInvitationLoading}
            onEdit={handleEditClick}
            onInvite={handleInviteToEventClick}
            onConnect={handleConnectClick}
          />
        </div>
        <div className={styles.profileContent}>
          <CompanyProfileCompletion
            t={t}
            user={user}
            company={company}
            selectedProfile={selectedProfile}
            progressPercentage={progressPercentage}
            onProfileChange={handleProfileChange}
          />

          {/* Desktop View - Original tabs */}
          <div className={styles.desktopTabs}>
            <SectionTabs
              sections={sections}
              onSectionChange={handleSectionChange}
            />
            {renderSectionContent()}
          </div>

          <CompanyProfileTabletSections
            t={t}
            company={company}
            selectedProfile={selectedProfile}
            collapsedMainSections={collapsedMainSections}
            onToggleMainSection={toggleMainSection}
            sectionIds={{
              general: SECTION_GENERAL_INFO,
              contact: SECTION_CONTACT_LOCATION,
              products: SECTION_PRODUCTS_SERVICES,
              networking: SECTION_NETWORKING,
            }}
          />
        </div>

        <CompanyProfileSidebar
          t={t}
          collapsedSections={collapsedSections}
          onToggleSection={toggleSection}
          companyGeneralActivity={companyGeneralActivity}
          companySector={companySector}
          companyOfficePhone={companyOfficePhone}
          showOfficePhone={showOfficePhone}
          companyOfficePhoneCountryCode={companyOfficePhoneCountryCode}
          companySecondaryPhone={companySecondaryPhone}
          showSecondaryPhone={showSecondaryPhone}
          companySecondaryPhoneCountryCode={companySecondaryPhoneCountryCode}
          showPhoneExtension={showPhoneExtension}
          companyExtension={companyExtension}
          companyEmail={companyEmail}
          showEmail={showEmail}
          companyWebsite={companyWebsite}
          showWebsite={showWebsite}
          companyTwitter={companyTwitter}
          showTwitter={showTwitter}
          companyYoutube={companyYoutube}
          showYoutube={showYoutube}
          companyFacebook={companyFacebook}
          showFacebook={showFacebook}
          companyLinkedin={companyLinkedin}
          showLinkedIn={showLinkedIn}
          companyInstagram={companyInstagram}
          showInstagram={showInstagram}
          keywords={keywords}
          currentLanguage={currentLanguage}
          certifications={certifications}
        />
      </div>

      <EventInvitationModal
        dialogRef={eventInvitationDialogRef}
        open={isEventInvitationModalOpen}
        onClose={handleCloseEventInvitation}
        onConfirm={handleEventInvitationConfirm}
        isLoading={eventInvitationLoading}
        events={availableEvents}
        senderCompanyRFC={user?.RFC}
        recipientCompanyRFC={company?.RFC}
      />

      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(4px)",
        }}
        open={loading || companyDataLoading || eventInvitationLoading}
      >
        <CircularProgress
          size={60}
          thickness={4}
          sx={{
            color: "#4eabe7",
          }}
        />
      </Backdrop>
    </div>
  );
};

/**
 * Export the CompanyProfile component as the default export.
 * This component is used as a page in the React Router configuration.
 */
export default CompanyProfile;
